/**
 * Device Fingerprint Service
 *
 * Core orchestrator that collects all device signals, generates a
 * composite fingerprint hash, and provides caching for the session.
 *
 * Usage:
 *   const result = await DeviceFingerprintService.collect();
 *   // result.fingerprintId — unique device hash
 *   // result.signals — raw signal data for querying
 *
 * The fingerprint is cached in memory for the session lifetime to avoid
 * redundant computation. Call `clearCache()` to force recollection.
 */

import {
  collectBrowserSignals,
  collectCanvasFingerprint,
  collectHardwareSignals,
  collectTimezoneSignals
} from './collectors';
import { generateSHA256Hash } from './hashUtils';
import type { DeviceFingerprintResult, DeviceFingerprintSignals } from './types';

const FINGERPRINT_VERSION = '1.0.0';

/** In-memory cache for the session */
let cachedResult: DeviceFingerprintResult | null = null;

/** Collect all device signals */
const collectAllSignals = async (): Promise<DeviceFingerprintSignals> => {
  const browserSignals = collectBrowserSignals();
  const hardwareSignals = collectHardwareSignals();
  const timezoneSignals = collectTimezoneSignals();
  const canvasDataUrl = collectCanvasFingerprint();
  const canvasHash = await generateSHA256Hash(canvasDataUrl);

  return {
    ...browserSignals,
    ...hardwareSignals,
    ...timezoneSignals,
    canvasHash
  };
};

/**
 * Generate a composite fingerprint hash from all collected signals.
 * All signals here are stable (unlikely to change between sessions
 * on the same device), making the hash a reliable device identifier.
 */
const generateFingerprintId = async (signals: DeviceFingerprintSignals): Promise<string> => {
  const components = [
    signals.userAgent,
    signals.platform,
    signals.language,
    signals.languages.join(','),
    signals.screenResolution.join('x'),
    String(signals.screenColorDepth),
    String(signals.devicePixelRatio),
    signals.timezone,
    String(signals.hardwareConcurrency),
    String(signals.deviceMemory),
    String(signals.maxTouchPoints),
    signals.canvasHash
  ];

  return generateSHA256Hash(components.join('|'));
};

/** Collect the device fingerprint. Returns cached result if available. */
const collect = async (): Promise<DeviceFingerprintResult> => {
  if (cachedResult) return cachedResult;

  const signals = await collectAllSignals();
  const fingerprintId = await generateFingerprintId(signals);

  cachedResult = {
    fingerprintId,
    signals,
    collectedAt: new Date().toISOString(),
    version: FINGERPRINT_VERSION
  };

  return cachedResult;
};

/** Clear the cached fingerprint. Forces recollection on next `collect()` call. */
const clearCache = (): void => {
  cachedResult = null;
};

/** Get the cached fingerprint without triggering collection. Returns null if not yet collected. */
const getCached = (): DeviceFingerprintResult | null => cachedResult;

export const DeviceFingerprintService = {
  collect,
  clearCache,
  getCached
} as const;
